import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { Contract, isNumber, isObject } from 'validata';
import { useAuthenticated } from '../auth';
import { useBoolFlag, useObjectFlag } from '../components/flags';
import { TRACK_FAULT_REQUEST_SETTINGS_FLAG, TRACK_FAULTS_UI_TEMPLATE_ENABLEMENT_FLAG } from '../feature-flags';
import { GetThingsFaultStatusResult, ThingFaultRequestSettings, ThingFaultStatus } from '../types/thing-fault-status';
import { getThingsFaultStatus } from '../util/thing-fault-status';

const FAULT_STATUS_STALE_TIME_MS = 2 * 60 * 1000;
const FAULT_STATUS_GC_TIME_MS = 10 * 60 * 1000;
const DEFAULT_FAULT_REQUEST_SETTINGS: ThingFaultRequestSettings = {
  dayRange: 30,
  batchSize: 100,
  concurrency: 2,
  limit: 500,
};

const faultRequestSettingsContract: Contract<ThingFaultRequestSettings> = {
  dayRange: isNumber(),
  batchSize: isNumber(),
  concurrency: isNumber(),
  limit: isNumber(),
};

const faultRequestSettingsValidator = isObject(faultRequestSettingsContract);

const isPositiveInteger = (value: number): boolean => Number.isInteger(value) && value > 0;

const resolveFaultRequestSettings = (
  settings: ThingFaultRequestSettings | undefined,
  range: number | undefined,
  batchSize: number | undefined,
): ThingFaultRequestSettings => {
  if (settings && Object.values(settings).every(isPositiveInteger)) {
    return settings;
  }

  return {
    dayRange: range ?? DEFAULT_FAULT_REQUEST_SETTINGS.dayRange,
    batchSize: batchSize ?? DEFAULT_FAULT_REQUEST_SETTINGS.batchSize,
    concurrency: DEFAULT_FAULT_REQUEST_SETTINGS.concurrency,
    limit: DEFAULT_FAULT_REQUEST_SETTINGS.limit,
  };
};

export interface UseThingsFaultStatusInput {
  thingIds: string[];
  range?: number;
  batchSize?: number;
}

export interface UseThingsFaultStatusOptions {
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
}

export const getThingsFaultStatusQueryKey = (
  input: UseThingsFaultStatusInput,
  settings: ThingFaultRequestSettings = resolveFaultRequestSettings(undefined, input.range, input.batchSize),
): (string | Record<string, unknown>)[] => {
  return [
    'things-fault-status',
    {
      thingIds: [...input.thingIds].sort(),
      dayRange: settings.dayRange,
      batchSize: settings.batchSize,
      concurrency: settings.concurrency,
      limit: settings.limit,
    },
  ];
};

export const useThingsFaultStatus = (
  input: UseThingsFaultStatusInput,
  options?: UseThingsFaultStatusOptions,
): UseQueryResult<GetThingsFaultStatusResult> => {
  const { restClient } = useAuthenticated();
  const faultsUiTemplateEnabled = useBoolFlag(TRACK_FAULTS_UI_TEMPLATE_ENABLEMENT_FLAG) ?? false;
  const faultRequestSettingsFlag = useObjectFlag(TRACK_FAULT_REQUEST_SETTINGS_FLAG, faultRequestSettingsValidator);
  const faultRequestSettings = useMemo(
    () => resolveFaultRequestSettings(faultRequestSettingsFlag, input.range, input.batchSize),
    [faultRequestSettingsFlag, input.batchSize, input.range],
  );

  const normalizedThingIds = useMemo(
    () => input.thingIds.map((thingId) => thingId.trim()).filter(Boolean),
    [input.thingIds],
  );

  const queryKey = useMemo(() => {
    return getThingsFaultStatusQueryKey({
      thingIds: normalizedThingIds,
    }, faultRequestSettings);
  }, [faultRequestSettings, normalizedThingIds]);

  const queryFn = useCallback(
    ({ signal }: { signal: AbortSignal }) => getThingsFaultStatus(restClient, {
      thingIds: normalizedThingIds,
      range: faultRequestSettings.dayRange,
      batchSize: faultRequestSettings.batchSize,
      concurrency: faultRequestSettings.concurrency,
      limit: faultRequestSettings.limit,
      signal,
    }),
    [restClient, normalizedThingIds, faultRequestSettings],
  );

  return useQuery({
    queryKey,
    queryFn,
    enabled: (options?.enabled ?? true) && faultsUiTemplateEnabled && normalizedThingIds.length > 0,
    staleTime: options?.staleTime ?? FAULT_STATUS_STALE_TIME_MS,
    gcTime: options?.gcTime ?? FAULT_STATUS_GC_TIME_MS,
    retry: false,
  });
};

export const createThingFaultStatusLookup = (items: ThingFaultStatus[] | undefined): Record<string, ThingFaultStatus> => {
  if (!items?.length) return {};

  return items.reduce<Record<string, ThingFaultStatus>>((acc, item) => {
    acc[item.thingId] = item;
    return acc;
  }, {});
};
